// Client-side Display & Layout preferences: layout density, horizontal side
// padding, text size, and the screen-edge padding floor (px, set from the
// Theme card). These drive the shared layout-token layer (see
// hooks/useLayoutTokens) and the typography scale (see theme.tsx) so every
// authenticated page reflows consistently.
//
// The three axes are deliberately orthogonal. Density controls *spacing* only
// (see docs/60 USER_GUIDE.md: Compact tightens spacing "without changing font
// sizes"); text size is the separate control for typography. Keeping them
// apart means a user who wants tighter padding at a readable size — or the
// reverse — is not forced to accept both.
//
// The context default is a real object with no-op setters (non-throwing), the
// same shape as ThemeContext rather than the throwing AnalyticsReferenceDate
// context. That lets PageShell and the settings section render outside a
// provider (e.g. in existing tests) and fall back to Comfortable + Standard
// instead of crashing. Values persist as raw strings under the kalpa- keys,
// matching kalpa-dark-mode / kalpa-timers-view-mode.
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type LayoutDensity = 'comfortable' | 'compact';
export type SidePadding = 'standard' | 'reduced' | 'edge';
export type TextSize = 'normal' | 'small';

export const LAYOUT_DENSITY_STORAGE_KEY = 'kalpa-layout-density';
export const SIDE_PADDING_STORAGE_KEY = 'kalpa-side-padding';
export const TEXT_SIZE_STORAGE_KEY = 'kalpa-text-size';
export const EDGE_PADDING_STORAGE_KEY = 'kalpa-edge-padding';

export const DEFAULT_LAYOUT_DENSITY: LayoutDensity = 'comfortable';
export const DEFAULT_SIDE_PADDING: SidePadding = 'standard';
export const DEFAULT_TEXT_SIZE: TextSize = 'normal';
// Minimum gutter (px) between page content and the screen edge — a floor
// applied on top of whichever side-padding preset is active, so titles keep a
// few pixels of breathing room even under Edge-to-edge. Set from the Theme card.
export const DEFAULT_EDGE_PADDING_PX = 8;
export const MIN_EDGE_PADDING_PX = 0;
export const MAX_EDGE_PADDING_PX = 48;

const LAYOUT_DENSITIES: readonly LayoutDensity[] = ['comfortable', 'compact'];
const SIDE_PADDINGS: readonly SidePadding[] = ['standard', 'reduced', 'edge'];
const TEXT_SIZES: readonly TextSize[] = ['normal', 'small'];

export interface LayoutPreferencesContextValue {
  layoutDensity: LayoutDensity;
  sidePadding: SidePadding;
  textSize: TextSize;
  edgePaddingPx: number;
  setLayoutDensity(density: LayoutDensity): void;
  setSidePadding(padding: SidePadding): void;
  setTextSize(size: TextSize): void;
  setEdgePaddingPx(px: number): void;
  resetLayoutPreferences(): void;
}

const isLayoutDensity = (value: unknown): value is LayoutDensity =>
  typeof value === 'string' && (LAYOUT_DENSITIES as string[]).includes(value);

const isSidePadding = (value: unknown): value is SidePadding =>
  typeof value === 'string' && (SIDE_PADDINGS as string[]).includes(value);

const isTextSize = (value: unknown): value is TextSize =>
  typeof value === 'string' && (TEXT_SIZES as string[]).includes(value);

const loadDensity = (): LayoutDensity => {
  try {
    const stored = localStorage.getItem(LAYOUT_DENSITY_STORAGE_KEY);
    if (isLayoutDensity(stored)) return stored;
  } catch {
    // Inaccessible localStorage should never break app startup.
  }
  return DEFAULT_LAYOUT_DENSITY;
};

const loadSidePadding = (): SidePadding => {
  try {
    const stored = localStorage.getItem(SIDE_PADDING_STORAGE_KEY);
    if (isSidePadding(stored)) return stored;
  } catch {
    // Inaccessible localStorage should never break app startup.
  }
  return DEFAULT_SIDE_PADDING;
};

const loadTextSize = (): TextSize => {
  try {
    const stored = localStorage.getItem(TEXT_SIZE_STORAGE_KEY);
    if (isTextSize(stored)) return stored;
  } catch {
    // Inaccessible localStorage should never break app startup.
  }
  return DEFAULT_TEXT_SIZE;
};

// Non-numeric junk falls back to the default; a numeric value is clamped into
// range rather than discarded, so an out-of-range hand-edit degrades gently.
const clampEdgePadding = (value: number): number =>
  Math.min(MAX_EDGE_PADDING_PX, Math.max(MIN_EDGE_PADDING_PX, Math.round(value)));

const loadEdgePadding = (): number => {
  try {
    const stored = localStorage.getItem(EDGE_PADDING_STORAGE_KEY);
    if (stored !== null) {
      const parsed = Number(stored);
      if (Number.isFinite(parsed)) return clampEdgePadding(parsed);
    }
  } catch {
    // Inaccessible localStorage should never break app startup.
  }
  return DEFAULT_EDGE_PADDING_PX;
};

const LayoutPreferencesContext = createContext<LayoutPreferencesContextValue>({
  layoutDensity: DEFAULT_LAYOUT_DENSITY,
  sidePadding: DEFAULT_SIDE_PADDING,
  textSize: DEFAULT_TEXT_SIZE,
  edgePaddingPx: DEFAULT_EDGE_PADDING_PX,
  setLayoutDensity: () => {},
  setSidePadding: () => {},
  setTextSize: () => {},
  setEdgePaddingPx: () => {},
  resetLayoutPreferences: () => {},
});

export const useLayoutPreferences = (): LayoutPreferencesContextValue =>
  useContext(LayoutPreferencesContext);

export const LayoutPreferencesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [layoutDensity, setLayoutDensity] = useState<LayoutDensity>(loadDensity);
  const [sidePadding, setSidePadding] = useState<SidePadding>(loadSidePadding);
  const [textSize, setTextSize] = useState<TextSize>(loadTextSize);
  const [edgePaddingPx, setEdgePaddingState] = useState<number>(loadEdgePadding);

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_DENSITY_STORAGE_KEY, layoutDensity);
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [layoutDensity]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDE_PADDING_STORAGE_KEY, sidePadding);
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [sidePadding]);

  useEffect(() => {
    try {
      localStorage.setItem(TEXT_SIZE_STORAGE_KEY, textSize);
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [textSize]);

  useEffect(() => {
    try {
      localStorage.setItem(EDGE_PADDING_STORAGE_KEY, String(edgePaddingPx));
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [edgePaddingPx]);

  const value = useMemo<LayoutPreferencesContextValue>(
    () => ({
      layoutDensity,
      sidePadding,
      textSize,
      edgePaddingPx,
      setLayoutDensity,
      setSidePadding,
      setTextSize,
      setEdgePaddingPx: (px: number) => setEdgePaddingState(clampEdgePadding(px)),
      resetLayoutPreferences: () => {
        setLayoutDensity(DEFAULT_LAYOUT_DENSITY);
        setSidePadding(DEFAULT_SIDE_PADDING);
        setTextSize(DEFAULT_TEXT_SIZE);
        setEdgePaddingState(DEFAULT_EDGE_PADDING_PX);
      },
    }),
    [layoutDensity, sidePadding, textSize, edgePaddingPx],
  );

  return (
    <LayoutPreferencesContext.Provider value={value}>
      {children}
    </LayoutPreferencesContext.Provider>
  );
};
